import { type DragEvent, useState } from "react";

// ---------------------------------------------------------------------------
// Shared drag-and-drop helpers for the facilitator agendas
//
// Native HTML5 drag-and-drop, driven by a small drag handle on each row so
// that dragging never interferes with editing the row's inputs. Used by both
// the per-module Agenda tab and the program-wide Agenda Blueprint, which share
// the same `fac-agenda-rows-<moduleId>` row model, defined in `@/lib/agenda`.
// ---------------------------------------------------------------------------

export type DropPos = "before" | "after";

/** Minimal shape the reorder logic needs from an agenda row. */
interface Reorderable {
  id: string;
  type: string;
  day: number;
}

/**
 * Reassign each non-day row's `day` to the nearest preceding day header, so the
 * `day` field stays consistent after rows are dragged across day boundaries.
 */
export function normalizeDays<T extends Reorderable>(rows: T[]): T[] {
  let currentDay: number | null = null;
  return rows.map((r) => {
    if (r.type === "day") {
      currentDay = r.day;
      return r;
    }
    return currentDay != null && r.day !== currentDay ? { ...r, day: currentDay } : r;
  });
}

/** Move the row `fromId` to just before/after the row `targetId`. */
export function moveRowById<T extends Reorderable>(
  rows: T[],
  fromId: string,
  targetId: string,
  pos: DropPos,
): T[] {
  if (!fromId || !targetId || fromId === targetId) {
    return rows;
  }
  const from = rows.findIndex((r) => r.id === fromId);
  if (from < 0) {
    return rows;
  }
  const next = [...rows];
  const [moved] = next.splice(from, 1);
  if (!moved) {
    return rows;
  }
  const target = next.findIndex((r) => r.id === targetId);
  if (target < 0) {
    return rows;
  }
  const insertIdx = pos === "after" ? target + 1 : target;
  next.splice(insertIdx, 0, moved);
  return normalizeDays(next);
}

interface DragState {
  dragId: string | null;
  overId: string | null;
  pos: DropPos;
}

const EMPTY: DragState = { dragId: null, overId: null, pos: "before" };

/**
 * Wire native drag-and-drop for a list of rows.
 *
 * - `handleProps(id)` → spread onto the small drag handle (only the handle is
 *   draggable, so row inputs stay editable).
 * - `rowProps(id)` → spread onto each row element (the drop target).
 * - `indicator(id)` → "before" | "after" | null, for drawing a drop line.
 *
 * `reorder` receives the move and should persist the new order.
 */
export function useAgendaDnd(reorder: (fromId: string, targetId: string, pos: DropPos) => void) {
  const [drag, setDrag] = useState<DragState>(EMPTY);

  const handleProps = (id: string) => ({
    draggable: true,
    onDragStart: (e: DragEvent<HTMLElement>) => {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", id);
      setDrag({ ...EMPTY, dragId: id });
    },
    onDragEnd: () => setDrag(EMPTY),
  });

  const rowProps = (id: string) => ({
    onDragOver: (e: DragEvent<HTMLElement>) => {
      if (!drag.dragId) {
        return;
      }
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      const rect = e.currentTarget.getBoundingClientRect();
      const pos: DropPos = e.clientY < rect.top + rect.height / 2 ? "before" : "after";
      if (drag.overId !== id || drag.pos !== pos) {
        setDrag((d) => ({ ...d, overId: id, pos }));
      }
    },
    onDrop: (e: DragEvent<HTMLElement>) => {
      e.preventDefault();
      if (drag.dragId) {
        reorder(drag.dragId, id, drag.pos);
      }
      setDrag(EMPTY);
    },
  });

  const indicator = (id: string): DropPos | null => {
    if (!drag.dragId || drag.overId !== id || drag.dragId === id) {
      return null;
    }
    return drag.pos;
  };

  return { dragId: drag.dragId, handleProps, rowProps, indicator };
}
